import {BaseApi} from '@/api/base-api';
import {getApiUrl, getTokenFromCookies} from '@/api/common-utils';
import {Button, GrayButton} from '@/components/common-components/v2/Button';
import {Checkbox} from '@/components/common-components/v2/Checkbox';
import {CurrentPageStore} from '@/store/content-optimizer/currentPage';
import {faArrowUpArrowDown, faSliders} from '@fortawesome/pro-regular-svg-icons';
import {faFileArrowDown, faFileLines, faFileSpreadsheet} from '@fortawesome/pro-light-svg-icons';
import {faPlus, faTrash} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Dropdown, Radio, Tooltip, Spin, Divider, Menu} from 'antd';
import {Dispatch, SetStateAction, useState} from 'react';
import styled from 'styled-components';
import {costPerClickFormatter, searchVolumeFormatter} from '../../../../../LeftSide/EditorSection/content-editor/utils';
import {LoadingOutlined} from '@ant-design/icons';
import {observer} from 'mobx-react';
import {Modal} from '@/components/common-components';
import {faGoogle} from '@fortawesome/free-brands-svg-icons';
import {CONTENT_ASSISTANT_API} from '@/api/ContentAssistant';
import {BulkRemoveTermsModal} from '../BulkRemoveTermsModal/BulkRemoveTermsModal';
import {toJS} from 'mobx';
import {useStore} from '@/store/root-store';
import {isScaPublicPage} from '@/utils/url';

interface TopicalProps {
  onViewChange?: Dispatch<SetStateAction<any>>;
  onChangeIncludeBranded?: Dispatch<SetStateAction<any>>;
  onTermsFilter?: Dispatch<SetStateAction<string[]>>;
  includeBranded: boolean;
  currentView?: 'clusters' | 'completion';
  store: CurrentPageStore;
  onSortTerms?: () => void;
}


export const TermsDropdownController = observer(({onViewChange, currentView, store, onChangeIncludeBranded, includeBranded, onTermsFilter, onSortTerms}: TopicalProps) => {
  const {Group} = Radio;
  const antIcon = <LoadingOutlined style={{fontSize: 14, marginRight: 10, color: '#7f4ead'}} spin />;
  const [showExportModal, setShowExportModal] = useState(false);
  const [showBulkRemoveTermsModal, setShowBulkRemoveTermsModal] = useState(false);
  const [exportToDocsLoading, setExportToDocsLoading] = useState(false);

  const {contentOptimizer: {currentPage}} = useStore('');

  /**
   *
   * @param {'include-more' | 'included' | 'include-less'} type shows type of focus term that we are calculating terms for
   * @return {number} number of terms of that typ
   */
  const getTermsCount = (type: 'include-more' | 'included' | 'include-less'): number => {
    if (!type) return 0;

    return toJS(currentPage)?.analytics?.focusTerms?.filter(item => item.status === type)?.length;
  };

  const [termsFiltersSetup, setTermsFiltersSetup] = useState({
    'included': {
      checked: true,
      type: 'included',
      label: 'Complete',
      color: '#1FAC47',
    },
    'include-more': {
      checked: true,
      type: 'include-more',
      label: 'Include more',
      color: '#F1AA3E',
    },
    'include-less': {
      checked: true,
      type: 'include-less',
      label: 'Reduce usage',
      color: '#F44343',
    },
  });

  const sorters = [
    {
      label: 'Importance',
      key: 'normalizedScore',
    },
    {
      label: 'Least used (Not used first)',
      key: 'leastCount',
    },
    {
      label: 'Most used',
      key: 'usedCount',
    },
    {
      label: 'Heading presence',
      key: 'foundIn',
    },
    {
      label: 'Search volume',
      key: 'sv',
    },
    {
      label: 'Cost-per-Click (CPC)',
      key: 'cpc',
    },
  ];


  const handleOnSortChange = value => {
    const sorter = {
      key: value.key,
      label: value.label,
    };

    store.setFocusTermsSorter(sorter);
    onSortTerms();
  };


  const onCheckboxClicked = (value: boolean) => {
    onChangeIncludeBranded(value);
  };

  const downloadFileToTxt = () => {
    const pageTitle = store?.content?.title ? `PAGE NAME: ${store.content?.title}` : '';
    const pageUrl = store?.content?.importedFromUrl ? `PAGE URL: ${store.content?.importedFromUrl}` : '';
    const articleUrl = store?.content?.url ? `ARTICLE URL: ${store.content?.url}` : '';

    const wordCount = `WORD COUNT: ${store?.content?.wordCount || 0}`;
    const imagesCount = `IMAGES: ${store?.analysis?.images?.count || 0}`;
    const videosCount = `VIDEOS: ${store?.analysis?.videos?.count || 0}`;
    const iframesCount = `IFRAMES: ${store?.analysis?.iframes?.count || 0}`;
    const internalLinksCount = `INTERNAL LINKS: ${store?.analysis?.links?.internal?.length || 0}`;
    const externallLinksCount = `SOCIAL: ${store?.analysis?.links?.external?.length || 0}`;
    const socialCount = `EXTERNAL LINKS: ${store?.analysis?.social?.count || 0} sources`;
    const javascriptCount = `JAVASCRIPT: ${store?.analysis?.js?.count || 0} scripts`;

    let headersSum = 0;
    store?.analysis?.headers?.header?.map(header => headersSum = headersSum + header.count);
    const headersCount = `HEADINGS: ${headersSum || 0}`;

    const pageTechnicalsString = store?.analysis?.url ?
      `##PAGE TECHNICALS \n\n${wordCount}\n${imagesCount}\n${headersCount}\n${videosCount}\n${iframesCount}\n${internalLinksCount}\n${externallLinksCount}\n${socialCount}\n${javascriptCount}` :
      '';


    const targetKeywords = store?.content?.targetKeywords?.length ? store?.content?.targetKeywords?.map(kw => `* ${kw.keyword.trim()}: SV: ${kw.sv}; CPC: ${kw.cpc}; KD: ${kw.kd}`).join('\n') : '';
    const includedFocusterms = [];
    const notIncludedFocusTerms = [];
    store?.analytics?.focusTerms?.length && store?.analytics?.focusTerms?.map(ft => {
      if (ft.usedCount > 0) {
        includedFocusterms.push(`* ${ft.name.trim()}: USED COUNT: ${ft.usedCount}/${ft.requiredCountMin}; SV: ${searchVolumeFormatter(ft.sv)}; CPC: ${costPerClickFormatter(ft.cpc)}`);
      } else {
        notIncludedFocusTerms.push(`* ${ft.name.trim()}: USED COUNT: ${ft.usedCount}/${ft.requiredCountMin}; SV: ${searchVolumeFormatter(ft.sv)}; CPC: ${costPerClickFormatter(ft.cpc)}`);
      }
    });
    const questionsSting = store?.analytics?.questions?.length ? store?.analytics?.questions?.map(q => `* ${q.keyword}: SV: ${q.sv}; CPC: ${q.cpc}; Used count: ${q.usedCount}`).join('\n') : '';


    const convertedText = `##PAGE INFORMATION \n\n${pageTitle}\n${pageUrl} \n${articleUrl} ${(pageTitle || pageUrl) && '\n\n\n\n'}${pageTechnicalsString} \n\n\n\n##TARGET KEWYRODS \n\n${targetKeywords}
    \n\n\n\n##QUESTIONS \n\n${questionsSting}\n\n\n\n##INCLUDED TOPICAL TERMS \n\n${includedFocusterms.join('\n')}  \n\n\n\n##NOT INCLUDED TOPICAL TERMS \n\n${notIncludedFocusTerms.join('\n')}`;

    const element = document.createElement('a');
    const file = new Blob([convertedText], {type: 'text/plain'});
    element.href = URL.createObjectURL(file);
    element.download = `${store?.content?.title || 'PegeExport'}.txt`;
    document.body.appendChild(element);
    element.click();
  };


  const getXlsExportLink = () => {
    const token = getTokenFromCookies();
    if (store?.content?.uuid) {
      const baseUrl = getApiUrl(BaseApi.CA_ENDPOINT, `/api/pages/${store?.content?.uuid}/export-data-to-xls/`);
      const savedKeywordsUrl = `${baseUrl}?&jwt=${token}`;
      return savedKeywordsUrl;
    }
  };

  const exportToGoogleDocs = async () => {
    setExportToDocsLoading(true);
    const response = await CONTENT_ASSISTANT_API.exportPageToGoogleDocs(store?.content?.uuid);
    if (response) {
      window.open(response, '_blank');
    }
    setExportToDocsLoading(false);
    setShowExportModal(false);
  };


  const termsFilterHandler = (checked: boolean, value: string) => {
    const updatedTermsSetup = {
      ...termsFiltersSetup,
      [value]: {
        checked: checked,
        label: termsFiltersSetup[value].label,
        color: termsFiltersSetup[value].color,
        type: termsFiltersSetup[value].type,
      },
    };

    setTermsFiltersSetup(updatedTermsSetup);

    const updatedFilteredTermsArr = [];
    Object.keys(updatedTermsSetup).forEach(item => {
      if (updatedTermsSetup[item].checked) {
        updatedFilteredTermsArr.push(item);
      }
    });
    onTermsFilter(updatedFilteredTermsArr);
  };
  const tooltipStyle = {backgroundColor: '#FFF', color: '#121212', borderRadius: '6px'};

  const isNotPublic = !isScaPublicPage();

  const viewChangeDropdown = <TermsViewDropdownContainer>
    <div style={{display: 'flex', flexDirection: 'column'}} onClick={e => e.stopPropagation()}>
      <Group value={currentView} onChange={e => onViewChange(e.target.value)} style={{display: 'flex', flexDirection: 'column'}}>
        <OverlayTitle>Editor settings:</OverlayTitle>
        <CheckboxStyledHighlights defaultChecked={currentPage?.highlights} onChange={e => currentPage?.setHighlights(e.target.checked)}><PTag>Enable Terms Highlighting in the Editor</PTag></CheckboxStyledHighlights>
        <OverlayTitle>Terms recommendations:</OverlayTitle>
        <CheckboxStyled defaultChecked={includeBranded} onChange={e => onCheckboxClicked(e.target.checked)}><PTag>Include Branded Terms</PTag></CheckboxStyled>

      </Group>
      {isNotPublic && <BulkRemoveTermsBtn onClick={() => setShowBulkRemoveTermsModal(true)}> <FontAwesomeIcon icon={faTrash} style={{marginRight: 10, marginLeft: 3}}/> Bulk Remove Terms</BulkRemoveTermsBtn>}
    </div>
  </TermsViewDropdownContainer>;

  return <TermsContainer>
    <BulkRemoveTermsModal isModalOpen={showBulkRemoveTermsModal} setIsModalOpen={setShowBulkRemoveTermsModal} />
    <Modal
      visible={showExportModal}
      style={{width: 680, margin: 'auto'}}
      onClose={() => setShowExportModal(false)}>
      <ExportModalTitle>Export Content Brief</ExportModalTitle>
      <ExportModalDesc>Export a brief with the SEO and Content data for this article (Meta Data, Word Count, Focus Terms, questions, Link Suggestions, etc.)</ExportModalDesc>
      <Divider />
      <ExportModalButtonsContainer>
        <GrayButton style={{margin: 0}} onClick={() => setShowExportModal(false)}>Close</GrayButton>
        <div style={{display: 'flex'}}>
          <GrayButton style={{margin: '0 5px'}} onClick={() => window.open(getXlsExportLink(), '_self')}>
            <FontAwesomeIcon icon={faFileSpreadsheet} color={'#1FAC47'} style={{marginRight: 10}}/> Save as .xls
          </GrayButton>
          <GrayButton style={{margin: '0 5px'}} onClick={() => downloadFileToTxt()}>
            <FontAwesomeIcon icon={faFileLines} color={'#2D6CCA'} style={{marginRight: 10}}/> Save as .txt
          </GrayButton>
          <GrayButton style={{margin: '0 5px'}} onClick={() => exportToGoogleDocs()} disabled={exportToDocsLoading}>
            {exportToDocsLoading ? <Spin indicator={antIcon} /> : <FontAwesomeIcon icon={faGoogle} color={'#2D6CCA'} style={{marginRight: 10}}/>} Open in  Google Docs
          </GrayButton>
        </div>
      </ExportModalButtonsContainer>
    </Modal>
    <TermsFilterContainer>
      <div style={{display: 'flex', alignItems: 'center'}}>
        {Object.keys(termsFiltersSetup).map((item, idx) => {
          return <Tooltip key={idx} title={termsFiltersSetup[item].label} overlayInnerStyle={tooltipStyle}>
            <CheckboxStyled
              customBackground={termsFiltersSetup[item].color}
              defaultChecked={termsFiltersSetup[item].checked}
              onChange={e => termsFilterHandler(e.target.checked, item)}>
              <span style={{fontSize: 11, color: '#A3A4A4', marginLeft: '4px'}}>{getTermsCount(termsFiltersSetup[item].type)}</span>
            </CheckboxStyled>
          </Tooltip>;
        })}
      </div>
      <GroupTermsContainer>
        <Tooltip title={`Sorted by: ${store.focusTermsSorter.label}`} overlayInnerStyle={tooltipStyle}>
          <Dropdown trigger={['click']} placement='bottomRight'
            overlay={
              <Menu>
                {sorters.map((item, index) => (
                  <Menu.Item onClick={() => handleOnSortChange(item)} key={index}>{item.label}</Menu.Item>
                ))}
              </Menu>
            }>
            <Sort>
              <FontAwesomeIcon icon={faArrowUpArrowDown} color='#A3A4A4' />
              <SortTxt>Sort</SortTxt>
            </Sort>
          </Dropdown>
        </Tooltip>

        {store?.isRemovingFocusTerms && <span><Spin indicator={antIcon} /></span>}
        <Tooltip title='Add focus terms' overlayInnerStyle={tooltipStyle}>
          <AddButton onClick={() => currentPage.setShowAddFocusTermModal(true)}>
            <FontAwesomeIcon icon={faPlus}/>
          </AddButton>
        </Tooltip>

        {isNotPublic && <Tooltip title='Export focus terms' overlayInnerStyle={tooltipStyle}> <ExportBtn variant='ghost' color='textGrayLight' onClick={() => setShowExportModal(true)}>
          <FontAwesomeIcon icon={faFileArrowDown} color='#A3A4A4' />
        </ExportBtn></Tooltip>}

        <Tooltip title='Focus terms settings' overlayInnerStyle={tooltipStyle}>
          <Dropdown overlay={viewChangeDropdown} placement='bottomLeft' trigger={['hover']}>
            <AdjustBtnContainer>
              <GearIcon><FontAwesomeIcon icon={faSliders} color='#A3A4A4' /></GearIcon>
            </AdjustBtnContainer>
          </Dropdown>
        </Tooltip>
      </GroupTermsContainer>
    </TermsFilterContainer>
  </TermsContainer>;
});

// TOPICAL TERMS STYLED COMPONENTES
const GearIcon = styled.a`
  height: 14px;
  cursor: pointer;
  margin-top: auto;
  margin-bottom: auto;
  display: block;
`;
const GroupTermsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TermsContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: transparent;
  padding: 10px 10px;
  background-color: #2D2F34;
  border-radius: 5px 5px 0 0;
`;

const ExportBtn = styled(Button)`
  background: transparent;
  font-size: 12px !important;
  height: 28px !important;
  margin-left: auto;
  margin-right: 0px;
  padding: 7px 10px;

  &:focus,
  &:hover {
    background: transparent;
  }
`;

const AdjustBtnContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  padding-bottom: 5px;
  cursor: default;
`;


const TermsViewDropdownContainer = styled.div`
  background-color: #fff;
  box-shadow: 0px 6px 25px rgba(0, 0, 0, 0.15);
  padding: 12px 20px;
  border-radius: 17px;
  margin-top: 10px;
  width: 232px;
`;

const CheckboxStyled = styled(Checkbox)`
// margin-left: 3px !important;
  display: flex;
  align-items: flex-start;
  font-weight: 500 !important;
  margin-right: 8px;

  .ant-checkbox-inner {
    width: 14px;
    height: 14px;
    padding: 7px;
    border-radius: 4px !important;
    ::after {
      top: 47% !important;
      left: 18.5% !important;
    }
  }

  .ant-checkbox {
    padding-right: 0px !important;
    padding-left: 0px !important;
  }

  span {
    padding-right: 1px;
    padding-left: 1px;
    font-size: 13px;
  }
`;

const PTag = styled.p`
margin-left: 10px;
margin-bottom: 0px !important;
`;

const CheckboxStyledHighlights = styled(CheckboxStyled)`
margin-bottom: 4px;
`;


const OverlayTitle = styled.p`
  color: #4E5156;
  font-size: 12px;
  margin-bottom:5px;
`;


const ExportModalTitle = styled.div`
  font-weight: 600;
  font-size: 20px;
  color: #121212;
  margin-bottom: 10px;
`;

const ExportModalDesc = styled.div`
  color: #4E5156;
`;

const ExportModalButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;
const BulkRemoveTermsBtn = styled.div`
  cursor: pointer;
  margin-top: 6px;
  font-weight: 500;
`;
const TermsFilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 103%;
  border-radius: 0px 4px 4px 0px;
  margin-left: -10px;
  padding: 0px 9px 10px 15px;
`;

const AddButton = styled.div`
  margin-left: 6px;
  cursor: pointer;
`;

const Sort = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const SortTxt = styled.span`
  color: var(--Text-gray-light, var(--Gray-light, #E8E8E8));
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;
